/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useAuth0 } from "@auth0/auth0-react";
import * as queryString from "query-string";
import CopyToClipboard from "react-copy-to-clipboard";
import { useNavigate } from "react-router-dom";
import { FlexContainer, SVGIcon, Text, THEME_OPTIONS } from "@nordcloud/gnui";
import { SUCCESS_TEXT } from "~/constants";
import { useGNUITheme } from "~/hooks";
import { ROUTES } from "~/routing/routes";
import { ACTIONS, Can } from "~/services/auth";
import { useCustomers } from "~/services/customers";
import { showSimpleInfo } from "~/services/toast";
import { getLastItem, isNil } from "~/tools";
import { CurrencySelector } from "../../CurrencySelector";
import {
  DropButton,
  DropItem,
  DropList,
  IconWrap,
  NotClickableDropItem,
} from "../../styles";

export function UserDropdown() {
  const { currentTheme, toggleTheme } = useGNUITheme();
  const { selectedCustomer, isAdminLite } = useCustomers();
  const { logout, user } = useAuth0();

  const userName = user?.name ?? "";

  const nextTheme =
    currentTheme === THEME_OPTIONS.LIGHT
      ? THEME_OPTIONS.DARK
      : THEME_OPTIONS.LIGHT;

  const navigate = useNavigate();

  const getCurrentUrlWithClientId = () => {
    const [location, queryParams] = window.location.href.split("?");
    const customerIdParam = {
      customerId: selectedCustomer?.id ?? "",
    };

    // we have no query params in URL so just adding `customerId` and we are good to go
    if (isNil(queryParams)) {
      // because our root path always registers as `/` and location has trailing slash on index
      const hasTrailingSlash = getLastItem(location.split("")) === "/";
      const separator = hasTrailingSlash ? "?" : "/?";
      return `${location}${separator}${queryString.stringify(customerIdParam)}`;
    }

    const parsedQueryParams = queryString.parse(queryParams);

    const newQueryParams = {
      ...parsedQueryParams,
      ...customerIdParam,
    };

    return `${location}/?${queryString.stringify(newQueryParams, {
      arrayFormat: "index",
    })}`;
  };

  return (
    <DropList>
      <NotClickableDropItem>
        <FlexContainer direction="column" alignItems="start">
          <Text size="sm" mb={0}>
            Logged in as {userName}
          </Text>
        </FlexContainer>
      </NotClickableDropItem>
      <DropItem>
        <CopyToClipboard
          text={getCurrentUrlWithClientId()}
          onCopy={() => {
            showSimpleInfo(SUCCESS_TEXT.copied);
          }}
        >
          <DropButton>
            <IconWrap>
              <SVGIcon name="copy" />
            </IconWrap>
            Copy link to current view
          </DropButton>
        </CopyToClipboard>
      </DropItem>
      <CurrencySelector />
      <DropItem>
        <DropButton onClick={() => navigate(ROUTES.releaseNotes)}>
          <IconWrap>
            <SVGIcon name="mailInformation" />
          </IconWrap>
          Release notes
        </DropButton>
      </DropItem>
      <DropItem>
        <DropButton
          onClick={() => {
            window.open(
              `https://docs.${
                isAdminLite ? "mca-lite" : "mca"
              }.nordcloudapp.com/`,
              "_blank",
              "noreferrer"
            );
          }}
        >
          <IconWrap>
            <SVGIcon name="listView" />
          </IconWrap>
          Documentation
        </DropButton>
      </DropItem>
      <Can action={ACTIONS.visitApiDocs}>
        <DropItem>
          <DropButton onClick={() => navigate(ROUTES.api)}>
            <IconWrap>
              <SVGIcon name="api" />
            </IconWrap>
            API Playground
          </DropButton>
        </DropItem>
      </Can>
      <DropItem>
        <DropButton data-cy="color-mode" onClick={toggleTheme}>
          <IconWrap>
            <SVGIcon name={nextTheme === THEME_OPTIONS.DARK ? "moon" : "sun"} />
          </IconWrap>
          Switch to {THEME_OPTIONS[nextTheme].toLowerCase()} mode
        </DropButton>
      </DropItem>
      <DropItem>
        <DropButton
          onClick={() => {
            logout({
              logoutParams: {
                returnTo: window.location.origin,
              },
            });
            window.localStorage.removeItem("klairtyCustomerId");
          }}
        >
          <IconWrap>
            <SVGIcon name="logout" />
          </IconWrap>
          Logout
        </DropButton>
      </DropItem>
    </DropList>
  );
}
